import { Trans } from "@lingui/react/macro";
import React from "react";
import {
  PageBuilderServiceRowsFragment,
  SanityServiceRow
} from "types/graphql-types";
import PageBuilderSectionTitle from "./PageBuilderSectionTitle";
import ServiceRow, {
  ServiceRowContent,
  ServiceRowFooter,
  ServiceRowImages,
  ServiceRowTitle
} from "../ServiceRow/ServiceRow";
import ServiceRows from "../ServiceRow/ServiceRows";
import { Button, ButtonsContainer } from "@silversea-ssc/ui-components-react";
import { BackgroundColor } from "types/componentBackground";
import { useGeolocation } from "../../utils/localization/geolocation/GeolocationContext";
import { matchesAny } from "../../utils/localization/geolocation";
import { graphql } from "gatsby";
import { stringPresent } from "src/utils/strings";
import { BlockContent } from "components/BlockContent";
import PageBuilderButtonCTA from "./PageBuilderButtonCTA";
import { isPresent } from "src/utils/checks";
import { cardColorForBackground } from "./pageBuilderRenderHelper";

export interface PageBuilderServiceRowsProps
  extends PageBuilderServiceRowsFragment {
  backgroundColor: BackgroundColor;
}

function rowHasContent({
  title,
  description
}: {
  title: SanityServiceRow["title"];
  description: SanityServiceRow["description"];
}) {
  return stringPresent(title?.localized) || description?.localized?.length > 0;
}

const PageBuilderServiceRows: React.FC<
  React.PropsWithChildren<PageBuilderServiceRowsProps>
> = ({ heading, services = [], backgroundColor }) => {
  const cardBackgroundColor = cardColorForBackground(backgroundColor);

  const geolocation = useGeolocation();
  const filteredRows = React.useMemo(() => {
    return services.filter((item): item is NonNullable<typeof item> => {
      if (!item) return false;

      return (
        rowHasContent({ title: item.title, description: item.description }) &&
        matchesAny(item.locationFilters, geolocation)
      );
    });
  }, [geolocation, services]);

  if (filteredRows.length == 0) {
    return null;
  }

  return (
    <React.Fragment>
      <PageBuilderSectionTitle {...heading} />
      <ServiceRows>
        {filteredRows.map((service, index) => {
          const images = service.images?.filter(isPresent);
          return (
            <ServiceRow
              key={service._key}
              variant={index % 2 ? "right" : "left"}
              backgroundColor={cardBackgroundColor}
            >
              {images && (
                <ServiceRowImages
                  items={images}
                  fallbackAlt={service.title?.localized}
                  backgroundColor={
                    cardBackgroundColor == "white" ? "white" : "gray"
                  }
                />
              )}

              {service.title?.localized && (
                <ServiceRowTitle tagline={service.tagline?.localized}>
                  {service.title.localized}
                </ServiceRowTitle>
              )}
              {service.description?.localized && (
                <ServiceRowContent>
                  <BlockContent value={service.description.localized} />
                </ServiceRowContent>
              )}
              {(service.link ||
                (service.ctaButtons && service.ctaButtons.length > 0)) && (
                <ServiceRowFooter>
                  <ButtonsContainer align="left">
                    {service.link && (
                      <Button
                        variant="text"
                        href={service.link?.fullPath}
                        iconName="arrow-right"
                        rel="nofollow"
                        dataContentPiece="more_button"
                      >
                        <Trans id="cta-discover-more">Discover more</Trans>
                      </Button>
                    )}

                    {service.ctaButtons && service.ctaButtons.length > 0 && (
                      <React.Fragment>
                        {service.ctaButtons.map((b) => (
                          <PageBuilderButtonCTA
                            key={b?._key}
                            defaultButtonVariant="text"
                            {...b}
                          />
                        ))}
                      </React.Fragment>
                    )}
                  </ButtonsContainer>
                </ServiceRowFooter>
              )}
            </ServiceRow>
          );
        })}
      </ServiceRows>
    </React.Fragment>
  );
};

export default PageBuilderServiceRows;

export const fragments = graphql`
  fragment PageBuilderServiceRows on SanityServiceRows {
    _key
    heading {
      ...SectionTitle
      ...SectionTitleCTAs
    }
    anchor
    services {
      _key
      title {
        localized(fallback: false)
      }

      tagline {
        localized(fallback: false)
      }

      description {
        localized(fallback: false)
      }

      link {
        fullPath
      }

      ctaButtons {
        ...ButtonCTA
      }

      images {
        __typename
        ... on SanityImageWithAlt {
          ...ImageWithAlt
        }

        ... on SanityVideoWithPosterImage {
          ...VideoWithPosterImage
        }
      }

      locationFilters {
        ...AirNonAirOrLocationFilter
      }
    }
  }
`;
